<template>
  <div v-if="visible" class="row">
    <div class="col-12">
      <section class="switch-card" :aria-labelledby="titleId">
        <!-- Vector miniature of the new dashboard (ES-1661, owner pick "B"). -->
        <div class="switch-card__visual" aria-hidden="true">
          <DashboardPreview class="switch-card__preview" />
        </div>
        <div class="switch-card__body">
          <span class="switch-card__badge">
            <span class="switch-card__dot" aria-hidden="true"></span>
            {{ $gettext("New") }}
          </span>
          <h2 :id="titleId" class="switch-card__title">
            {{ $gettext("Discover the new version of Enroll Talent") }}
          </h2>
          <p class="switch-card__text">
            {{
              $gettext(
                "Enjoy a modernized interface and our new features. You can switch back to the classic version at any time."
              )
            }}
          </p>
          <div class="switch-card__actions">
            <!-- aria-disabled, not disabled: focus stays on the button while pending. -->
            <button
              ref="cta"
              type="button"
              class="switch-card__button switch-card__button--primary"
              :aria-disabled="pending ? 'true' : null"
              @click="switchPlatform"
            >
              <BSpinner v-if="pending" small aria-hidden="true" />
              {{ $gettext("Try the new version") }}
            </button>
            <button
              type="button"
              class="switch-card__button switch-card__button--secondary"
              :aria-disabled="pending ? 'true' : null"
              @click="dismiss"
            >
              {{ $gettext("Later") }}
            </button>
          </div>
          <p v-if="failed" role="alert" class="switch-card__error">
            {{
              $gettext(
                "We couldn't match your account on this version of the platform. Please contact our team for help."
              )
            }}
          </p>
          <span role="status" class="sr-only">{{
            pending ? $gettext("Redirecting…") : ""
          }}</span>
        </div>
        <button
          type="button"
          class="switch-card__close"
          :aria-label="$gettext('Close')"
          :aria-disabled="pending ? 'true' : null"
          @click="dismiss"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </section>
    </div>
  </div>
</template>

<script>
import { BSpinner } from "bootstrap-vue";
import DashboardPreview from "./DashboardPreview.vue";
import { mapActions, mapGetters } from "vuex";
import { isDismissed, recordDismissal } from "./switchPlatformCardDismissal";

export default {
  components: { BSpinner, DashboardPreview },
  data() {
    // Read storage before the first render so a dismissed card never flashes.
    return {
      visible: !isDismissed(),
      pending: false,
      failed: false,
      titleId: "switch-platform-card-title"
    };
  },
  computed: {
    ...mapGetters("me", ["lg"])
  },
  methods: {
    ...mapActions("me", { requestSwitchPlatform: "switchPlatform" }),
    async switchPlatform() {
      if (this.pending) {
        return;
      }
      this.pending = true;
      this.failed = false;
      try {
        const { url } = await this.requestSwitchPlatform({
          target: "enroll_talent",
          lg: this.lg
        });
        if (typeof url !== "string" || url === "") {
          throw new Error("switch-platform returned no url");
        }
        // Pending stays on: the page is leaving.
        window.location.assign(url);
      } catch (error) {
        this.pending = false;
        this.failed = true;
        this.$nextTick(() => {
          if (this.$refs.cta) {
            this.$refs.cta.focus();
          }
        });
      }
    },
    dismiss() {
      if (this.pending) {
        return;
      }
      recordDismissal();
      this.visible = false;
      this.$emit("dismissed");
    }
  }
};
</script>

<style scoped>
.switch-card {
  position: relative;
  display: grid;
  /* Capped: on wide screens the visual column must not stretch (owner, 2026-09-16). */
  grid-template-columns: minmax(300px, 440px) minmax(0, 1fr);
  background: #fff;
  border: 1px solid #e5e5e2;
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 20px;
  box-shadow: 0 12px 16px -4px rgba(0, 0, 0, 0.08),
    0 4px 6px -2px rgba(0, 0, 0, 0.03), 0 2px 2px -1px rgba(0, 0, 0, 0.04);
  font-family: Inter, "Helvetica", Arial, sans-serif;
}

.switch-card__preview {
  position: absolute;
  top: 28px;
  left: 30px;
  width: 560px;
  max-width: none;
  height: auto;
  filter: drop-shadow(0 24px 30px rgba(20, 8, 60, 0.4));
}

.switch-card__visual {
  position: relative;
  min-height: 190px;
  overflow: hidden;
  background: linear-gradient(135deg, #5c19be, #8761ea);
}

.switch-card__body {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  padding: 24px 56px 24px 24px;
}

.switch-card__badge {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 10px 2px 8px;
  border-radius: 999px;
  background: #f5f3ff;
  box-shadow: inset 0 0 0 1px #dbd4ff;
  color: #5c19be;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
}

.switch-card__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #8761ea;
}

/* Reset the paper-dashboard h2/p sizes, weights and margins. */
.switch-card__title {
  margin: 0;
  color: #171714;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  text-wrap: balance;
}

.switch-card__text {
  max-width: 52ch;
  margin: 0;
  color: #53534c;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.switch-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 8px;
}

/* Native buttons: the app's .btn forces uppercase, margins and outline:0. */
.switch-card__button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 14px;
  border: 0;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
  cursor: pointer;
}

.switch-card__button:focus-visible,
.switch-card__close:focus-visible {
  outline: 2px solid #6c38d2;
  outline-offset: 2px;
}

/* DR-02: no UA ring on a mouse click, keyboard focus keeps it (focus-visible above). */
.switch-card__button:focus:not(:focus-visible),
.switch-card__close:focus:not(:focus-visible) {
  outline: none;
}

.switch-card__button--primary {
  background: #6c38d2;
  color: #fff;
  box-shadow: 0 1px 2px rgba(10, 13, 18, 0.05),
    inset 0 -2px 0 rgba(10, 13, 18, 0.05);
}

.switch-card__button--primary:hover {
  background: #5c19be;
}

.switch-card__button--secondary {
  background: #fff;
  color: #40403a;
  box-shadow: inset 0 0 0 1px #d4d4d1, 0 1px 2px rgba(10, 13, 18, 0.05);
}

.switch-card__button--secondary:hover {
  background: #fafaf8;
  color: #171714;
}

.switch-card__button[aria-disabled="true"],
.switch-card__close[aria-disabled="true"] {
  cursor: default;
}

/* DR-07: the inert secondary controls read disabled while the switch is pending; the
   primary button stays fully opaque (it carries the spinner + label). */
.switch-card__button--secondary[aria-disabled="true"],
.switch-card__close[aria-disabled="true"] {
  opacity: 0.5;
}

.switch-card__error {
  margin: 0;
  color: #b42318;
  font-size: 14px;
  line-height: 20px;
}

.switch-card__close {
  position: absolute;
  top: 8px;
  right: 8px;
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: none;
  color: #72726a;
  cursor: pointer;
}

.switch-card__close:hover {
  background: #fafaf8;
  color: #53534c;
}

@media (max-width: 767px) {
  .switch-card {
    grid-template-columns: minmax(0, 1fr);
  }

  /* The visual stays: it is the card's signature, reduced to a band. */
  .switch-card__visual {
    min-height: 112px;
  }

  .switch-card__preview {
    top: 20px;
    left: 20px;
    width: 430px;
  }

  .switch-card__body {
    padding: 20px;
  }

  .switch-card__button {
    flex: 1 1 auto;
    min-height: 44px;
  }

  /* Over the gradient and the white window the X needs its own chip. */
  .switch-card__close {
    background: #fff;
    box-shadow: 0 1px 2px rgba(10, 13, 18, 0.05);
    color: #53534c;
  }
}

/* G · motion: BSpinner ships its own infinite CSS animation, unreachable without ::v-deep. */
@media (prefers-reduced-motion: reduce) {
  .switch-card__button ::v-deep .spinner-border {
    animation: none;
  }
}
</style>
